import React from 'react';
import { BackArrow } from '@assets/icons/svgs/BackArrow';
import styled, { css } from 'styled-components';
import { breakpoints } from '@constants/breakpoints';

// The arrow itself, shared by both layouts below so it looks the same whether it floats over the
// page or sits at the left of an action bar.
const arrowStyles = css`
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  svg {
    height: 24px;
    width: 24px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;

// Default layout: the arrow floats in the page's top-left padding, outside the content flow. Every
// caller that passes no `actions` gets exactly this, unchanged.
const IconWrapper = styled.div`
  position: absolute;
  top: 16px;
  left: ${({ Left }) => Left || '16px'};
  z-index: 5;

  ${arrowStyles}

  @media (max-width: ${() => breakpoints.mobile}) {
    top: 4px;
    left: 4px;
  }
`;

// `actions` layout. This bar is laid out IN FLOW rather than floated like the bare arrow above:
// the actions are real buttons that wrap onto a second row on a narrow screen, and a floated row
// would sit on top of the page content rather than push it down. Pages that pass actions therefore
// want less top spacing under the button than the 48px the floating arrow needs.
const ActionBar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  /* One button tall, so a page carrying actions on some routes only keeps the arrow in one place. */
  min-height: 40px;
`;

const BarIcon = styled.div`
  ${arrowStyles}
  flex-shrink: 0;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
`;

// `actions` — an optional list of rendered elements (buttons, usually), right-aligned and vertically
// centred against the arrow. Passing it at all switches to the bar layout, so `[]` is a meaningful
// value: it holds the bar's place on a route that has no actions of its own. `left` applies to the
// floating layout only — the bar takes its position from the flow.
export default function BackArrowButton({ onClick, left, actions, 'aria-label': ariaLabel }) {
  if (actions != null) {
    return (
      <ActionBar>
        <BarIcon onClick={onClick} aria-label={ariaLabel}>
          <BackArrow />
        </BarIcon>
        {/* toArray keys the list, so a caller can pass a plain array of elements without keys. */}
        <Actions>{React.Children.toArray(actions)}</Actions>
      </ActionBar>
    );
  }

  return (
    <IconWrapper onClick={onClick} Left={left} aria-label={ariaLabel}>
      <BackArrow />
    </IconWrapper>
  );
}
